import type { SoaLineItemsMeta } from "@/components/tables/types";

export interface SoaApiColumn {
  id: number;
  name: string;
}

export interface SoaDisplayColumn {
  id: number;
  /** Canonical display header (e.g. DATE, AMOUNT). */
  name: string;
  /** Row object key from the API column definition. */
  dataKey: string;
}

const SOA_COLUMN_DEFINITIONS = [
  { header: "DATE", aliases: ["date", "bate"] },
  { header: "ORIGIN", aliases: ["origin", "from"] },
  { header: "DESTINATION", aliases: ["destination", "to"] },
  {
    header: "WAYBILL",
    aliases: ["waybill", "waybill number", "way bill#", "way bill"],
  },
  { header: "REMARKS", aliases: ["remarks"] },
  {
    header: "PLATE NUMBER",
    aliases: ["plate number", "plate no", "plt#", "plt"],
  },
  {
    header: "CONTAINER NUMBER",
    aliases: ["container number", "container no", "container#"],
  },
  { header: "SIZE", aliases: ["size"] },
  { header: "AMOUNT", aliases: ["amount"] },
  { header: "12% VAT", aliases: ["12% vat", "12%vat", "vat"] },
] as const;

const normalizeColumnKey = (value: string): string =>
  value.toLowerCase().trim();

/** Restrict and reorder API columns to the canonical SOA table structure. */
export function buildSoaDisplayColumns(
  apiColumns: SoaApiColumn[],
): SoaDisplayColumn[] {
  const usedApiColumnIds = new Set<number>();

  return SOA_COLUMN_DEFINITIONS.map((definition, index) => {
    const apiColumn = apiColumns.find((column) => {
      if (usedApiColumnIds.has(column.id)) return false;
      const normalizedName = normalizeColumnKey(column.name);
      return (
        definition.aliases.some(
          (alias) => normalizeColumnKey(alias) === normalizedName,
        ) || normalizeColumnKey(definition.header) === normalizedName
      );
    });

    if (apiColumn) {
      usedApiColumnIds.add(apiColumn.id);
    }

    return {
      id: apiColumn?.id ?? -(index + 1),
      name: definition.header,
      dataKey: apiColumn?.name ?? definition.header,
    };
  });
}

export const isSoaNumericColumn = (header: string): boolean => {
  const lower = header.toLowerCase().trim();
  return lower === "amount" || lower.includes("vat");
};

/** Map a display header to the corresponding all-pages total from API meta. */
export const getSoaFooterTotalForColumn = (
  header: string,
  meta: SoaLineItemsMeta | null,
): number | null => {
  if (!meta) return null;
  const lower = header.toLowerCase().trim();
  if (lower.includes("vat")) {
    return meta.total_vat ?? null;
  }
  if (lower === "amount") {
    return meta.total_amount ?? null;
  }
  return null;
};

const parseNumericCellValue = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : Number.NaN;
  }
  if (typeof value === "string" && value !== "") {
    return Number.parseFloat(value.replace(/,/g, ""));
  }
  return Number.NaN;
};

/** Fallback: sum AMOUNT column across a full in-memory dataset (client-side pagination). */
export const sumSoaAmountColumn = (
  rows: Record<string, unknown>[],
  columns: SoaDisplayColumn[],
): number => {
  const amountColumn = columns.find((column) => column.name === "AMOUNT");
  if (!amountColumn) return 0;

  return rows.reduce((sum, row) => {
    const num = parseNumericCellValue(row[amountColumn.dataKey]);
    return Number.isFinite(num) ? sum + num : sum;
  }, 0);
};

export const getSoaCellValue = (
  row: Record<string, unknown>,
  column: SoaDisplayColumn,
): unknown => row[column.dataKey];
